import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useBulkDeleteFacts } from '@/api/mutations/useBulkDeleteFacts';
import { useIndexedDb } from '@/hooks/useIndexedDb';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

export function ArchiveSelectedButton({
  selectedFactIds,
  setSelectedFactIds,
}: {
  selectedFactIds: Set<string>;
  setSelectedFactIds: (ids: Set<string>) => void;
}) {
  const { subsetId } = useParams();
  if (!subsetId) return null;

  const [open, setOpen] = useState(false);

  const { getAllSelectedFactIds, deselectMultipleFacts } = useIndexedDb();
  const { mutate: deleteFacts, isLoading: isDeletingFacts } = useBulkDeleteFacts(subsetId);

  const handleDeleteAll = async () => {
    const factIds = await getAllSelectedFactIds(subsetId);
    trackCustomEvent(TrackingEventsV2.BulkDeleteFacts, { subsetId, count: factIds.length });
    deleteFacts(
      { factIds },
      {
        onSuccess: () => {
          deselectMultipleFacts(subsetId, factIds);
          setSelectedFactIds(new Set());
          setOpen(false);
        },
      },
    );
  };

  return (
    <>
      <Button onClick={() => setOpen(!open)} disabled={selectedFactIds.size === 0}>
        <Icon className="lucide--archive" />
        Archive {selectedFactIds.size} {selectedFactIds.size === 1 ? 'Fact' : 'Facts'}
      </Button>
      <ConfirmationModal
        isOpen={open}
        onOpenChange={setOpen}
        title="Archive Facts"
        onConfirm={handleDeleteAll}
        isLoading={isDeletingFacts}
      >
        <p>
          Are you sure you want to archive all selected facts? This will prevent these facts from appearing in any
          subset.
        </p>
        <p className="mt-3">Note that any references to these facts will still exist.</p>
      </ConfirmationModal>
    </>
  );
}
